import client from '@xFrame4/business/GraphQlClient';
import { PaymentGatewayCode } from '@xFrame4/common/Constants';
import config, { Environment } from '@config';
import { store } from '@redux/store';

export class PaymentGateway
{
    code: PaymentGatewayCode;

    constructor(code: PaymentGatewayCode)
    {
        this.code = code;
    }

    /**
     * Is the payment processed online (card payment, TBI, PayPal, etc.)?
     */
    get isOnline()
    {
        let onlinePaymentGateways = [
            PaymentGatewayCode.CardGeneric,
            PaymentGatewayCode.CardStripe,
            PaymentGatewayCode.CardEuPlatesc,
            PaymentGatewayCode.CardPaylike,
            PaymentGatewayCode.CardNetopia,
            PaymentGatewayCode.TbiPay
        ];

        return onlinePaymentGateways.includes(this.code);
    }

    static factory(code: PaymentGatewayCode)
    {
        switch (code)
        {
            case PaymentGatewayCode.ClickAndCollectCash:
                return new ClickAndCollectCashPaymentGateway();
            case PaymentGatewayCode.ClickAndCollectCard:
                return new ClickAndCollectCardPaymentGateway();
            case PaymentGatewayCode.CourierCash:
                return new CourierCashPaymentGateway();
            case PaymentGatewayCode.BankTransfer:
                return new BankTransferPaymentGateway();
            case PaymentGatewayCode.CardGeneric:
                return new CardGenericPaymentGateway();
            case PaymentGatewayCode.CardStripe:
                return new StripePaymentGateway();
            case PaymentGatewayCode.CardEuPlatesc:
                return new EuPlatescPaymentGateway();
            case PaymentGatewayCode.CardPaylike:
                return new PaylikePaymentGateway();
            case PaymentGatewayCode.CardNetopia:
                return new NetopiaPaymentGateway();
            case PaymentGatewayCode.TbiPay:
                return new TbiPaymentGateway();
        }
    }
}

export class ClickAndCollectCashPaymentGateway extends PaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.ClickAndCollectCash);
    }
}

export class ClickAndCollectCardPaymentGateway extends PaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.ClickAndCollectCard);
    }
}

export class CourierCashPaymentGateway extends PaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.CourierCash);
    }
}

export class BankTransferPaymentGateway extends PaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.BankTransfer);
    }
}

export class CardGenericPaymentGateway extends PaymentGateway
{
    constructor(code: PaymentGatewayCode = PaymentGatewayCode.CardGeneric)
    {
        super(code);
    }
}

export class StripePaymentGateway extends CardGenericPaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.CardStripe);
    }

    /**
     * Create a Stripe Checkout Session.
     * 
     * @param amount The payment amount (in major currency units, e.g., dollars, euros)
     * @param currency Three-letter ISO currency code (e.g., 'usd', 'eur', 'ron')
     * @param orderId The order ID (string)
     * @param successUrl URL to redirect to after successful payment
     * @param cancelUrl URL to redirect to if payment is cancelled
     * @param customerEmail Optional customer email to pre-fill in the checkout form
     * @param description Optional payment description
     * @returns Object containing success status, session ID, and checkout URL
     */
    async createCheckoutSession(amount: number, currency: string, orderId: string, successUrl: string, cancelUrl: string, customerEmail?: string, description?: string)
    {
        const { gql } = await import('@apollo/client');
        
        let query = `
        mutation StripeCreateCheckoutSession($amount: Decimal!, $currency: String!, $orderId: String!, $successUrl: String!, $cancelUrl: String!, $customerEmail: String, $description: String) {
            stripeCreateCheckoutSession(amount: $amount, currency: $currency, orderId: $orderId, successUrl: $successUrl, cancelUrl: $cancelUrl, customerEmail: $customerEmail, description: $description) {
              success
              sessionId
              checkoutUrl
            }
        }
        `;
        
        let { data } = await client.mutate({
            mutation: gql(query),
            variables: {
                amount: amount,
                currency: currency,
                orderId: orderId,
                successUrl: successUrl,
                cancelUrl: cancelUrl,
                customerEmail: customerEmail,
                description: description
            }
        });

        return {
            success: data?.stripeCreateCheckoutSession?.success ?? false,
            sessionId: data?.stripeCreateCheckoutSession?.sessionId,
            checkoutUrl: data?.stripeCreateCheckoutSession?.checkoutUrl
        };
    }

    /**
     * Get the Stripe publishable key from configuration.
     * This key is used on the client side to initialize Stripe.js
     * 
     * @returns The Stripe publishable key
     */
    getPublishableKey(): string
    {
        return config.stripePublishableKey ?? "";
    }
}

export class EuPlatescPaymentGateway extends CardGenericPaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.CardEuPlatesc);
    }

    /**
     * Send an authorization request to the euplatesc.ro endpoint. 
     * Return a link to page on which the user can pay with the card.
     * After the payment, euplatesc.ro will send an IPN to the server.
     * 
     * @param amount The payment amount.
     * @param currency 3-letter currency code
     * @param orderId The order ID.
     * @param orderDescription Payment description.
     * @param successUrl Redirect to this URL after successful payment.
     * @param failedUrl Redirect to this URL after failed payment.
     * @returns The payment link where the user can pay with card. Null if the request was not succesful.
     */
    async sendAuthorizationRequest(amount: number, currency: string, orderId: string, 
        orderDescription: string = '', successUrl: string = '', failedUrl: string = ''
    )
    {
        const { gql } = await import('@apollo/client');
        
        let query = `
        mutation EuPlatescSendAuthorizationRequest($amount: Decimal!, $currency: String!, $orderId: String!, $orderDescription: String, $successUrl: String, $failedUrl: String) {
            euPlatescSendAuthorizationRequest(amount: $amount, currency: $currency, orderId: $orderId, orderDescription: $orderDescription, successUrl: $successUrl, failedUrl: $failedUrl) {
              success
              paymentLink
            }
          }
        `;
        
        let { data } = await client.mutate({
            mutation: gql(query),
            variables: {
                amount: amount,
                currency: currency,
                orderId: orderId,
                orderDescription: orderDescription,
                successUrl: successUrl,
                failedUrl: failedUrl
            }
        });

        if (data?.euPlatescSendAuthorizationRequest?.success)
        {
            return data.euPlatescSendAuthorizationRequest.paymentLink as string;
        }
        else
        {
            return null;
        }
    }
}

export class PaylikePaymentGateway extends CardGenericPaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.CardPaylike);
    }

    /**
     * Shows a Paylike popup. 
     * The resulted transaction needs to be captured on the server side (using the transaction ID).
     * https://github.com/paylike/api-docs
     * https://github.com/paylike/sdk
     * 
     * @param amountAsMajor The payment amount with 2 decimal places. https://github.com/paylike/api-docs/blob/master/money.md
     * @param currency 3 letter currency code. Eg: USD, RON, HUF.
     * @param currencyExponent https://github.com/paylike/api-docs/blob/master/money.md
     * @param title 
     * @param description 
     * @param custom
     */
    showPopup(amountAsMajor: number, currency: string, title: string, description: string = '', currencyExponent: number = 2, custom: any = null): Promise<string | null>
    {
        return new Promise(resolve =>
        {
            // config
            let amountAsMinor = Math.round(amountAsMajor * Math.pow(10, currencyExponent));
            let paylikeConfig = {
                amount: { currency: currency, exponent: currencyExponent, value: amountAsMinor },
                title: title,
                description: description,
                locale: store.getState().layout.language.locale,

                // data to pass along (objects, nested objects, arrays and primitives)
                // visible in your dashboard
                // can be extracted using the API
                // keep it below 50K
                custom: custom,
                test: false
            }
            paylikeConfig.test = config.environment != Environment.Production; //Make sure to use a key from a test account and set the test parameter, which will also allow you to test without https (e.g. locally).

            //show card payment popup
            let paylike = Paylike({ key: config.paylikePublicKey });
            let paylikePopup = paylike.pay(paylikeConfig, (error: any, response: any) =>
            {
                //handle error and result
                if (error == null && response != null && response.transaction != null)
                {
                    resolve(response.transaction.id);
                }
                else
                {
                    console.log('Paylike: ' + error);
                    resolve(null);
                }
            });

            //3D secure iframe scrolling enabled. This has to be checked once in a while, because PayLike may update the Web SDK.
            try
            {
                if (paylikePopup != null)
                {
                    //paylikePopup.querySelector('div.tds-iframe iframe').attr('scrolling', true as unknown as string)
                }
            }
            catch (error)
            {
                console.log(error);
            }
        });
    }

    /**
     * Capture a Paylike payment transaction for an order.
     * The transaction needs to be captured together with an order to prevent fraud.
     * The captured amount has to be the order total amount.
     * 
     * @param transactionId The Paylike authorization transaction id.
     * @param orderId The order ID.
     */
    async captureTransactionForOrder(orderId: number, transactionId: string)
    {
        const { gql } = await import('@apollo/client');
        
        let query = `
        mutation PaylikeCaptureTransactionForOrder($orderId: Int!, $transactionId: String!) {
            paylikeCaptureTransactionForOrder(orderId: $orderId, transactionId: $transactionId) {
              success
            }
          }
        `;
        
        let { data } = await client.mutate({
            mutation: gql(query),
            variables: {
                orderId: orderId,
                transactionId: transactionId
            }
        });

        return data?.paylikeCaptureTransactionForOrder?.success as boolean;
    }
}

export class NetopiaPaymentGateway extends CardGenericPaymentGateway
{
    constructor()
    {
        super(PaymentGatewayCode.CardNetopia);
    }

    /**
     * Start a Netopia payment request.
     * 
     * @param amount The payment amount.
     * @param currency 3-letter currency code.
     * @param orderId The order ID.
     * @param orderDescription Payment description.
     * @param userEmail The user email.
     * @param userPhone The user phone.
     * @param redirectUrl The redirect URL. The URL to which the user will be redirected after the payment.
     * @param cancelUrl The cancel URL. The URL to which the user will be redirected if the payment is cancelled.
     * @returns The payment URL. Null if the request was not successful.
     */
    async startPaymentRequest(amount: number, currency: string, orderId: string, orderDescription: string, userEmail: string, userPhone: string, redirectUrl: string, cancelUrl?: string)
    {
        const { gql } = await import('@apollo/client');
        
        let query = `
        mutation NetopiaStartPaymentRequest($amount: Decimal!, $currency: String!, $orderId: String!, $orderDescription: String, $userEmail: String, $userPhone: String, $redirectUrl: String, $cancelUrl: String) {
            netopiaStartPaymentRequest(amount: $amount, currency: $currency, orderId: $orderId, orderDescription: $orderDescription, userEmail: $userEmail, userPhone: $userPhone, redirectUrl: $redirectUrl, cancelUrl: $cancelUrl) {
              success
              paymentUrl
            }
        }
        `;
        
        let { data } = await client.mutate({
            mutation: gql(query),
            variables: {
                amount: amount,
                currency: currency,
                orderId: orderId,
                orderDescription: orderDescription,
                userEmail: userEmail,
                userPhone: userPhone,
                redirectUrl: redirectUrl,
                cancelUrl: cancelUrl
            }
        });

        if (data?.netopiaStartPaymentRequest?.success)
        {
            return data.netopiaStartPaymentRequest.paymentUrl as string;
        }
        else
        {
            return null;
        }
    }
}

export class TbiPaymentGateway extends PaymentGateway
{
    /** The minimum amount that can be financed is 1000 RON. */
    static MinimumAmount = 1000;

    constructor()
    {
        super(PaymentGatewayCode.TbiPay);
    }

    /**
     * Call the TbiPay Finalize endpoint.
     * Returns a link to a page where the client can apply for a loan. 
     * After the loan application has been processed, TBI will send an IPN to the server.
     * 
     * @param orderId A unique order ID.
     * @param orderTotal The total order amount.
     * @param customerData Customer data (cnp, email, etc.)
     * @param orderLines Order item lines in TbiPay format.
     * @returns The loan application URL. Null if the call was not successful.
     */
    async callFinalize(orderId: number, orderTotal: number, customerData: TbiPayCustomer, orderLines: TbiPayOrderItem[])
    {
        const { gql } = await import('@apollo/client');
        
        let query = `
        mutation TbiPayCallFinalize($orderId: Int!, $orderTotal: Decimal!, $customerData: JSONString!, $orderLines: JSONString!) {
            tbiPayCallFinalize(orderId: $orderId, orderTotal: $orderTotal, customerData: $customerData, orderLines: $orderLines) {
              success
              loanApplicationUrl
            }
          }
        `;
        
        let { data } = await client.mutate({
            mutation: gql(query),
            variables: {
                orderId: orderId,
                orderTotal: orderTotal,
                customerData: JSON.stringify(customerData),
                orderLines: JSON.stringify(orderLines)
            }
        });

        if (data?.tbiPayCallFinalize?.success)
        {
            return data.tbiPayCallFinalize.loanApplicationUrl as string;
        }
        else
        {
            return null;
        }
    }
}

export type TbiPayCustomer = {
    email: string,
    cnp?: string
}

export type TbiPayOrderItem = {
    qty: number,
    price: number,
    name?: string,
    imagelink?: string,
    category?: string,
    sku?: string
}

export default PaymentGateway;